import { useState } from "react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Icon } from "../ui/Icon";
import { clearSession } from "../auth/token";
import { useSession } from "../auth/session";
import { TopBar } from "./TopBar";
import { FloorSectionPrompt } from "../floor-sections/FloorSectionPrompt";
import { clearStoredSections } from "../floor-sections/floor-sections";

// SAME ORDER, LABELS, ICONS AND VISIBILITY AS `src/app/_layout/_menu/app.menu.component.ts`:
// CALENDAR IS HIDDEN FROM MANAGERS, HOST ACTIVITY VIEW IS SHOWN ONLY TO THEM, AND SETTINGS IS
// HIDDEN FROM EVERYONE (`visible: false`) THOUGH ITS ROUTE STILL WORKS.
type MenuItem = { label: string; icon: string; to: string; hideForManager?: true; managerOnly?: true; hidden?: true };
const MENU: MenuItem[] = [
  { label: "Dashboard", icon: "dashboard", to: "/" },
  { label: "Daily Tasks", icon: "task_alt", to: "/daily-tasks" },
  { label: "Calendar", icon: "calendar_month", to: "/calendar", hideForManager: true },
  { label: "In-Play", icon: "communities", to: "/in-play" },
  { label: "Players", icon: "recent_actors", to: "/players" },
  { label: "Events Manager", icon: "event", to: "/events-manager" },
  { label: "Player Communications", icon: "forum", to: "/player-communications" },
  { label: "Opportunity Center", icon: "bolt", to: "/opportunity-center" },
  { label: "Host Activity View", icon: "monitor_heart", to: "/host-activity-view", managerOnly: true },
  // VISIBLE TO HOSTS TOO: THE PAGE RENDERS READ-ONLY WITHOUT THE MANAGER ROLE
  { label: "Notification Rules", icon: "notifications_active", to: "/notification-rules" },
  { label: "Notifications", icon: "notifications", to: "/notifications" },
  { label: "Reporting", icon: "lab_profile", to: "/reporting" },
  { label: "Settings", icon: "settings", to: "/settings", hidden: true },
];

export function AppShell() {
  // COLLAPSED BY DEFAULT ON EVERY WIDTH, OPENING AS AN OVERLAY - MATCHES THE
  // ANGULAR DEFAULTS (`menuMode: "overlay"`, `overlayMenuActive: false`) AND THE
  // REVIEWED MOCKUP. MUI'S TEMPORARY DRAWER CLOSES ON ESCAPE AND BACKDROP CLICK.
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const isManager = session.ready && session.hasRole("PILLARS-HOST-MANAGER");

  const logout = () => {
    // THE NEXT SIGN-IN IN THIS BROWSER IS ASKED FOR ITS SECTIONS AGAIN
    clearStoredSections();
    clearSession();
    queryClient.clear();
    void navigate({ to: "/login" });
  };

  const isActive = (to: string) => (to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(to + "/"));

  return (
    // ANGULAR'S SHELL CLASSES, SO `_content.scss`/`_responsive.scss` (IMPORTED IN
    // `styles/pillars-layout.scss`) GIVE THE PAGE ITS ORIGINAL PADDING AT EVERY WIDTH
    <div className="layout-wrapper layout-overlay">
      <TopBar onMenu={() => setMenuOpen(true)} onLogout={logout} />
      <FloorSectionPrompt />

      {/* `.layout-sidebar` IN OVERLAY MODE: A 300PX FULL-HEIGHT PANEL, 1PX BORDER, 12PX
          RIGHT CORNERS, THE SOFT SHADOW, 0.5REM 1.5REM PADDING; THE MASK IS `--maskbg` */}
      <Drawer
        anchor="left"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        slotProps={{
          backdrop: { sx: { backgroundColor: "var(--maskbg)" } },
          paper: {
            sx: {
              width: 300,
              p: "0.5rem 1.5rem",
              borderRadius: "0 12px 12px 0",
              border: 1,
              borderColor: "divider",
              boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.02), 0px 0px 2px rgba(0, 0, 0, 0.05), 0px 1px 4px rgba(0, 0, 0, 0.08)",
            },
          },
        }}
      >
        <Box role="navigation" aria-label="Main">
          {/* THE ANGULAR MENU'S ROOT GROUP HAS AN EMPTY LABEL, SO NO HEADING */}
          <List disablePadding sx={{ py: "0.75rem", "& .MuiListItemButton-root": { p: "0.75rem 1rem" }, "& .MuiListItemIcon-root": { minWidth: 0, mr: "0.5rem", color: "inherit" }, "& .MuiListItemText-root": { m: 0 } }}>
            {MENU.filter((item) => !item.hidden && !(item.hideForManager && isManager) && !(item.managerOnly && !isManager)).map((item) => (
              <ListItemButton key={item.label} component={Link} to={item.to} selected={isActive(item.to)} onClick={() => setMenuOpen(false)}>
                <ListItemIcon>
                  <Icon name={item.icon} />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      <div className="layout-main-container">
        <main className="layout-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
